import { GetStaticPaths, GetStaticProps } from 'next'
import { FC } from 'react'
import { initEnvironment } from '@lifedot/relay'
import { fetchQuery } from 'relay-runtime'
import { postPage_indexQuery } from '@/queries/__generated__/postPage_indexQuery.graphql'
import {
  postPageBuildTargetQuery,
  postPageIndexQuery,
  postPagePreQuery
} from '@/queries/postPage'
import { useQuery } from 'relay-hooks/lib/useQuery'
import { NotFound } from '@lifedot/components/NotFound'
import { postPage_preQuery } from '@/queries/__generated__/postPage_preQuery.graphql'
import { PostWithListTemplate } from '@/templates/PostWithListTemplate/PostWithListTemplate'
import { ListTemplate } from '@/templates/ListTemplate/ListTemplate'
import { PostTemplate } from '@/templates/PostTemplate/PostTemplate'
import { PostSeoHead } from '@/components/Seo/post/PostSeoHead'
import { ShortCodeContextProvider } from '@/hooks/use-short-code-data/ShortCodeContextProvider'
import { postPage_buildTargetQuery } from '@/queries/__generated__/postPage_buildTargetQuery.graphql'
import { PostDummyTemplate } from '@/templates/PostTemplate/PostDummyTemplate'
import {
  getShortCodeCemeteryIds,
  getShortCodeSlugs
} from '@/utils/shortcode-params-parser'
import { SingleColumnPostTemplate } from '@/templates/SigleColumnPostTemplate/SigleColumnPostTemplate'

export const getStaticPaths: GetStaticPaths<{ slug: string }> = async () => {
  if (!['production'].includes(process.env.NEXT_PUBLIC_BUILD_ENV ?? ''))
    return {
      paths: [],
      fallback: 'blocking'
    }

  const { environment }: ReturnType<typeof initEnvironment> = initEnvironment()
  let slugs: string[] = []
  let hasNext = true
  let from = 0
  while (hasNext) {
    const res = await fetchQuery<postPage_buildTargetQuery>(
      environment,
      postPageBuildTargetQuery,
      { from },
      {}
    ).toPromise()
    if (!res) continue
    slugs = [
      ...slugs,
      ...res.listPosts.items
        .filter(({ redirect_to }) => !redirect_to)
        .map(({ slug }) => slug)
    ]
    hasNext = res.listPosts.pagination.hasNext
    from = res.listPosts.pagination.to + 1
  }

  return {
    paths: slugs.map((slug) => ({ params: { slug } })),
    fallback: 'blocking'
  }
}

export interface PostPageProps {
  slug: string
  shortCodeCemeteryIds: number[]
  shortCodeSlugs: string[]
  templateName: string
  parentSlug: string
}

export const getStaticProps: GetStaticProps<
  PostPageProps,
  { slug: string }
> = async ({ params }) => {
  const slug = params?.slug

  if (slug === undefined) {
    return {
      notFound: true
    }
  }

  try {
    const { environment, relaySSR }: ReturnType<typeof initEnvironment> =
      initEnvironment()

    const prepareRes = await fetchQuery<postPage_preQuery>(
      environment,
      postPagePreQuery,
      {
        slug
      }
    ).toPromise()

    if (prepareRes?.post?.status !== 'published')
      return {
        notFound: true
      }

    if (prepareRes.post.redirect_to)
      return {
        redirect: {
          statusCode: 301,
          destination: prepareRes.post.redirect_to
        },
        revalidate: Number(process.env.DEFAULT_REVALIDATE_TERM)
      }

    const shortCodeCemeteryIds = getShortCodeCemeteryIds(
      prepareRes.post.shortcodes
    )
    const shortCodeSlugs = getShortCodeSlugs(prepareRes.post.shortcodes)
    const parentSlug = prepareRes.post?.parent_post?.slug || ''

    await fetchQuery<postPage_indexQuery>(
      environment,
      postPageIndexQuery,
      {
        slug,
        page: { from: 0, size: 20 },
        shortCodeCemeteryIds,
        requestShortCodeCemeteryIdSize: shortCodeCemeteryIds.length + 1,
        shortCodeSlugs,
        requestShortCodeSlugSize: shortCodeSlugs.length + 1,
        parentSlug: [parentSlug]
      },
      {}
    ).toPromise()

    const [, relayData] = await relaySSR.getCache()

    const [cacheString, payload] = relayData
    if (!payload.data)
      return {
        notFound: true
      }

    return {
      props: {
        slug,
        shortCodeCemeteryIds,
        shortCodeSlugs,
        templateName: prepareRes.post.template_name,
        parentSlug,
        relayData:
          !relayData || !('json' in payload)
            ? null
            : [[cacheString, payload.json]]
      },
      revalidate: Number(process.env.DEFAULT_REVALIDATE_TERM)
    }
  } catch (e) {
    if (typeof e === 'object' && e !== null && 'res' in e) {
      if (typeof (e as { res?: unknown }).res === 'object')
        console.log((e as { res?: { errors?: unknown } }).res?.errors)
    } else if (e instanceof Error) console.log(e.message)

    return {
      props: {
        slug,
        shortCodeCemeteryIds: [0],
        shortCodeSlugs: ['none'],
        templateName: 'show_post',
        parentSlug: ''
      },
      revalidate: 1
    }
  }
}

const PostPage: FC<PostPageProps> = ({
  slug,
  shortCodeCemeteryIds,
  shortCodeSlugs,
  templateName,
  parentSlug
}) => {
  const { error, data, isLoading } = useQuery<postPage_indexQuery>(
    postPageIndexQuery,
    {
      slug,
      page: { size: 20, from: 0 },
      shortCodeCemeteryIds,
      requestShortCodeCemeteryIdSize: shortCodeCemeteryIds.length + 1,
      shortCodeSlugs,
      parentSlug: [parentSlug],
      requestShortCodeSlugSize: shortCodeSlugs.length + 1
    }
  )

  if (isLoading) return <PostDummyTemplate />
  if (error || !data?.post) return <NotFound />

  return (
    <>
      <PostSeoHead {...data} />
      <ShortCodeContextProvider
        cemeteryFragment={data.shortCodeCemeteries}
        postFragment={data.shortCodePosts}
      >
        {templateName === 'show_category' ? (
          <ListTemplate data={data} />
        ) : templateName === 'show_category_article' ? (
          <PostWithListTemplate data={data} />
        ) : templateName === 'show_post_special_feature' ? (
          <SingleColumnPostTemplate data={data} />
        ) : (
          <PostTemplate data={data} />
        )}
      </ShortCodeContextProvider>
    </>
  )
}

export default PostPage
